import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from './config'
import { SPRITE_MANIFEST } from './sprites'
import { GameScene } from './GameScene'
import { attachLongPress } from './input/attachLongPress'

export class BootScene extends Phaser.Scene {
  private bearHeadZone!: Phaser.GameObjects.Zone
  private demoHotTubZone!: Phaser.GameObjects.Zone
  private testingUnlocked = false

  constructor() {
    super({ key: 'BootScene' })
  }

  preload() {
    // Load all sprites from manifest
    console.log('Loading sprites...')
    
    SPRITE_MANIFEST.forEach(sprite => {
      this.load.image(sprite.key, sprite.path)
    })

    // Load sound effects
    const sfxNames = ['throw', 'splash', 'explosion', 'electric', 'snowman-death',
      'wave-start', 'wave-complete', 'coin', 'place-tower', 'upgrade', 'game-over', 'button-click']
    sfxNames.forEach(name => {
      this.load.audio(`sfx-${name}`, `sfx/${name}.mp3`)
    })

    // Create loading progress bar
    const progressBar = this.add.graphics()
    const progressBox = this.add.graphics()
    const width = GAME_WIDTH * 0.6
    const height = 20
    const x = (GAME_WIDTH - width) / 2
    const y = GAME_HEIGHT / 2 - 10

    progressBox.fillStyle(0x222222)
    progressBox.fillRect(x, y, width, height)

    this.load.on('progress', (value: number) => {
      progressBar.clear()
      progressBar.fillStyle(0x4CAF50)
      progressBar.fillRect(x + 2, y + 2, (width - 4) * value, height - 4)
    })

    this.load.on('complete', () => {
      progressBar.destroy()
      progressBox.destroy()
      console.log('All sprites loaded!')
    })
  }

  create() {
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('scene') === 'demo') {
      this.scene.start('DemoScene')
      return
    }
    this.testingUnlocked = false
    const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2

    // Snowy bg
    this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0xdce8f0)

    // Title
    this.add.text(cx, 100, '🐻 Hot Tub Defense 🐻', {
      fontSize: '46px',
      fontFamily: 'Arial, sans-serif',
      color: '#3E2723',
      fontStyle: 'bold',
    }).setOrigin(0.5)

    this.add.text(cx, 160, 'Bears. Hot Tubs. Sharks.', {
      fontSize: '22px',
      fontFamily: 'Arial, sans-serif',
      color: '#5D4037',
      fontStyle: 'italic',
    }).setOrigin(0.5)

    // Show bear and tub sprites instead of drawn graphics
    this.showBearAndTubSprites(cx, 310, 1.4)

    // Secret: long-press bear head for 3s = testing mode.
    this.bearHeadZone = this.add.zone(cx - 50, 265, 80, 80).setInteractive().setDepth(60)
    attachLongPress(this, this.bearHeadZone, {
      onComplete: () => {
        this.testingUnlocked = true
        const flash = this.add.text(cx, 240, '🔧 TESTING MODE 🔧', {
          fontSize: '20px', fontFamily: 'Arial', color: '#E91E63', fontStyle: 'bold',
        }).setOrigin(0.5)
        this.tweens.add({ targets: flash, alpha: 0, y: 220, duration: 1500 })
      },
    })

    // Secret: long-press the visible hot tub for 3s = Tower V2 demo.
    // This zone is deliberately separated from the bear-head testing cheat.
    this.demoHotTubZone = this.add.zone(cx + 45, 325, 70, 80).setInteractive().setDepth(60)
    attachLongPress(this, this.demoHotTubZone, {
      onComplete: () => {
        const flash = this.add.text(cx, 415, '✨ TOWER LAB ✨', {
          fontSize: '20px', fontFamily: 'Arial', color: '#E91E63', fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(100)
        this.tweens.add({ targets: flash, alpha: 0, duration: 180 })
        this.time.delayedCall(180, () => this.scene.start('DemoScene'))
      },
    })

    // Snowman enemies preview using sprites
    this.showSnowmanSprite(cx - 180, 330, 0.8, 'snowman-basic')
    this.showSnowmanSprite(cx + 180, 330, 0.8, 'snowman-fast')
    this.showSnowmanSprite(cx - 140, 370, 0.6, 'snowman-tank')
    this.showSnowmanSprite(cx + 140, 370, 0.6, 'snowman-boss')

    const hasSave = GameScene.hasSave()

    if (hasSave) {
      // Continue button (primary)
      const contBtn = this.add.rectangle(cx, 480, 220, 55, 0x4CAF50).setInteractive({ useHandCursor: true }).setDepth(50)
      this.add.rectangle(cx, 480, 220, 55).setStrokeStyle(3, 0x388E3C).setDepth(50)
      this.add.text(cx, 480, '♨️  CONTINUE  ♨️', {
        fontSize: '26px',
        fontFamily: 'Arial, sans-serif',
        color: '#fff',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(51)

      contBtn.on('pointerover', () => contBtn.setFillStyle(0x66BB6A))
      contBtn.on('pointerout', () => contBtn.setFillStyle(0x4CAF50))
      contBtn.on('pointerdown', () => this.scene.start('GameScene', { testingMode: this.testingUnlocked, loadSave: true }))

      // New Game button (secondary, smaller)
      const newBtn = this.add.rectangle(cx, 550, 180, 40, 0x5D4037).setInteractive({ useHandCursor: true }).setDepth(50)
      this.add.rectangle(cx, 550, 180, 40).setStrokeStyle(2, 0x3E2723).setDepth(50)
      this.add.text(cx, 550, '🔄 New Game', {
        fontSize: '20px',
        fontFamily: 'Arial, sans-serif',
        color: '#fff',
      }).setOrigin(0.5).setDepth(51)

      newBtn.on('pointerover', () => newBtn.setFillStyle(0x795548))
      newBtn.on('pointerout', () => newBtn.setFillStyle(0x5D4037))
      newBtn.on('pointerdown', () => {
        GameScene.clearSave()
        this.scene.start('GameScene', { testingMode: this.testingUnlocked })
      })

      this.add.text(cx, 595, 'Tower Defense • Protect the Lodge!', {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#78909C',
      }).setOrigin(0.5)
    } else {
      // No save — original play button
      const btn = this.add.rectangle(cx, 500, 220, 55, 0x4CAF50).setInteractive({ useHandCursor: true }).setDepth(50)
      this.add.rectangle(cx, 500, 220, 55).setStrokeStyle(3, 0x388E3C).setDepth(50)
      this.add.text(cx, 500, '♨️  DEFEND  ♨️', {
        fontSize: '26px',
        fontFamily: 'Arial, sans-serif',
        color: '#fff',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(51)

      btn.on('pointerover', () => btn.setFillStyle(0x66BB6A))
      btn.on('pointerout', () => btn.setFillStyle(0x4CAF50))
      btn.on('pointerdown', () => this.scene.start('GameScene', { testingMode: this.testingUnlocked }))

      this.add.text(cx, 570, 'Tower Defense • Protect the Lodge!', {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#78909C',
      }).setOrigin(0.5)
    }
  }

  showBearAndTubSprites(x: number, y: number, _scale: number) {
    // Tub: 601x712 native → target ~120px wide
    const tubSprite = this.add.image(x + 30, y, 'tub-honey')
    tubSprite.setScale(0.18)
    tubSprite.setDepth(5)

    // Bear: 272x530 native → target ~60px wide
    const bearSprite = this.add.image(x - 50, y - 10, 'bear-honey')
    bearSprite.setScale(0.18)
    bearSprite.setDepth(6)
  }

  showSnowmanSprite(x: number, y: number, scale: number, spriteKey: string) {
    // Snowmen: ~750x870 native → target ~50px wide
    const snowmanSprite = this.add.image(x, y, spriteKey)
    snowmanSprite.setScale(0.06 * scale)
    snowmanSprite.setDepth(5)
  }
}
